import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Stack,
  Divider,
  Card
} from '@mui/material';
import {
  History as HistoryIcon,
  Tune as AdjustIcon,
  CardGiftcard as GiftIcon,
  AddCircleOutline as PlusIcon,
  RemoveCircleOutline as MinusIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import api from '../api/api';

const PRIMARY_COLOR = '#4f46e5';

// Helper para parsear errores de FastAPI (422) a un string plano y seguro
const parseErrorMessage = (err, defaultMsg) => {
  const detail = err.response?.data?.detail;
  if (!detail) return defaultMsg;

  if (Array.isArray(detail)) {
    return detail.map(e => `${e.loc ? e.loc.join('.') + ': ' : ''}${e.msg}`).join(' | ');
  }

  if (typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  return String(detail);
};

const PuntosMovimientosModal = ({ open, onClose, clienteId, clienteNombre, idSucursal }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [movimientos, setMovimientos] = useState([]);
  const [premios, setPremios] = useState([]);
  const [cuenta, setCuenta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  // 2. Añadir la función para manejar la descarga en el componente
const handleExportExcel = async () => {
  try {
    const response = await api.get(
      `/puntos-movimientos/cliente/${clienteId}/exportar-excel`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `movimientos_puntos_${clienteNombre || clienteId}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    showSnackbar('Excel descargado correctamente');
  } catch (err) {
    console.error(err);
    showSnackbar('Error al exportar el historial a Excel', 'error');
  }
};

  // Formulario de Ajuste Manual
  const [ajusteForm, setAjusteForm] = useState({
    puntos: '',
    tipo: 'SUMAR', // 'SUMAR' o 'RESTAR'
    concepto: ''
  });

  // Formulario de Canje
  const [selectedPremio, setSelectedPremio] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Cargar Saldo de Cuenta (Manejo silencioso si el cliente no tiene cuenta aún)
  const fetchCuenta = useCallback(async () => {
    if (!clienteId) return;
    try {
      const res = await api.get(`/puntos-cuenta/cliente/${clienteId}`);
      setCuenta(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setCuenta({ puntos_acumulados: 0, puntos_canjeados: 0 });
      } else {
        console.error('Error al obtener cuenta:', err);
      }
    }
  }, [clienteId]);

  // Cargar Historial de Movimientos
  const fetchMovimientos = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const res = await api.get(`/puntos-movimientos/cliente/${clienteId}`);
      setMovimientos(res.data || []);
    } catch (err) {
      showSnackbar(parseErrorMessage(err, 'Error al cargar historial'), 'error');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  // Cargar Catálogo de Premios
  const fetchPremios = useCallback(async () => {
    try {
      const res = await api.get('/puntos-incentivos/?solo_activos=true');
      setPremios(res.data || []);
    } catch (err) {
      console.error('Error al cargar premios:', err);
    }
  }, []);

  useEffect(() => {
    if (open && clienteId) {
      fetchCuenta();
      fetchMovimientos();
      fetchPremios();
      setTabIndex(0);
    }
  }, [open, clienteId, fetchCuenta, fetchMovimientos, fetchPremios]);

  // Guardar Ajuste Manual
  const handleSaveAjuste = async () => {
    if (!ajusteForm.puntos || Number(ajusteForm.puntos) <= 0 || !ajusteForm.concepto.trim()) {
      showSnackbar('Ingresa una cantidad válida y un concepto', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const puntosFinales = ajusteForm.tipo === 'RESTAR' 
        ? -Math.abs(Number(ajusteForm.puntos)) 
        : Math.abs(Number(ajusteForm.puntos));

      const payload = {
        id_cliente: Number(clienteId),
        id_sucursal: Number(idSucursal),
        tipo_movimiento: 'AJUSTE_MANUAL',
        puntos: puntosFinales,
        concepto: ajusteForm.concepto.trim()
      };

      await api.post('/puntos-movimientos/', payload);
      showSnackbar('Ajuste registrado exitosamente');
      setAjusteForm({ puntos: '', tipo: 'SUMAR', concepto: '' });
      await fetchCuenta();
      await fetchMovimientos();
      setTabIndex(0);
    } catch (err) {
      showSnackbar(parseErrorMessage(err, 'Error al registrar ajuste'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Guardar Canje de Premio
  const handleSaveCanje = async () => {
    const premioObj = premios.find(p => p.id_incentivo === selectedPremio);
    if (!premioObj) {
      showSnackbar('Selecciona un premio válido', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id_cliente: Number(clienteId),
        id_sucursal: Number(idSucursal),
        tipo_movimiento: 'CANJE',
        puntos: Number(premioObj.puntos_requeridos),
        concepto: `Canje de premio: ${premioObj.nombre}`
      };

      await api.post('/puntos-movimientos/', payload);
      showSnackbar('Canje registrado exitosamente');
      setSelectedPremio('');
      await fetchCuenta();
      await fetchMovimientos();
      setTabIndex(0);
    } catch (err) {
      showSnackbar(parseErrorMessage(err, 'Error al procesar canje'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const saldoDisponible = cuenta ? (cuenta.puntos_acumulados - cuenta.puntos_canjeados) : 0;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTipoChip = (tipo, puntos) => {
    switch (tipo) {
      case 'ACUMULACION':
        return <Chip label={`+${puntos} Pts`} color="success" size="small" variant="outlined" />;
      case 'CANJE':
        return <Chip label={`-${puntos} Pts`} color="error" size="small" variant="outlined" />;
      case 'AJUSTE_MANUAL':
        return (
          <Chip 
            label={`${puntos >= 0 ? '+' : ''}${puntos} Pts`} 
            color={puntos >= 0 ? 'info' : 'warning'} 
            size="small" 
            variant="outlined" 
          />
        );
      default:
        return <Chip label={`${puntos} Pts`} size="small" />;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Movimientos de Puntos
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {clienteNombre || `Cliente ID: ${clienteId}`}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: PRIMARY_COLOR, color: '#fff', px: 2, py: 0.8, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" display="block" sx={{ opacity: 0.85, fontSize: '0.68rem' }}>
                SALDO DISPONIBLE
              </Typography>
              <Typography variant="h6" fontWeight={800} lineHeight={1}>
                {saldoDisponible} pts
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabIndex} onChange={(e, val) => setTabIndex(val)} indicatorColor="primary">
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Historial" />
            <Tab icon={<AdjustIcon />} iconPosition="start" label="Ajuste Manual" />
            <Tab icon={<GiftIcon />} iconPosition="start" label="Canjear Premio" />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ minHeight: 320 }}>
          {/* TAB 0: HISTORIAL DE MOVIMIENTOS */}
          {tabIndex === 0 && (
            loading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : movimientos.length === 0 ? (
              <Typography align="center" color="text.secondary" py={4}>
                No hay movimientos registrados para este cliente.
              </Typography>
            ) : (
              <Box>
                {/* BOTÓN EXPORTAR */}
                <Box display="flex" justifyContent="flex-end" mb={1.5}>
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                  >
                    Exportar a Excel
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movimientos.map((row) => (
                        <TableRow key={row.id_movimiento}>
                          <TableCell>{row.id_movimiento}</TableCell>
                          <TableCell>{getTipoChip(row.tipo_movimiento, row.puntos)}</TableCell>
                          <TableCell>{row.concepto || 'Sin concepto'}</TableCell>
                          <TableCell>{row.usuario_registro?.nombre_usuario || 'Sistema'}</TableCell>
                          <TableCell>{formatDate(row.fecha_movimiento)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          )}

          {/* TAB 1: AJUSTE MANUAL */}
          {tabIndex === 1 && (
            <Box sx={{ maxWidth: 450, mx: 'auto', py: 2 }}>
              <Typography variant="subtitle2" mb={2}>
                Registrar un ajuste manual sobre los puntos del cliente
              </Typography>

              <Stack spacing={2}>
                <TextField
                  select
                  label="Tipo de Ajuste"
                  fullWidth
                  size="small"
                  value={ajusteForm.tipo}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, tipo: e.target.value })}
                >
                  <MenuItem value="SUMAR">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <PlusIcon color="success" fontSize="small" /> Sumar Puntos
                    </Stack>
                  </MenuItem>
                  <MenuItem value="RESTAR">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <MinusIcon color="error" fontSize="small" /> Restar Puntos
                    </Stack>
                  </MenuItem>
                </TextField>

                <TextField
                  label="Cantidad de Puntos"
                  type="number"
                  fullWidth
                  size="small"
                  value={ajusteForm.puntos}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, puntos: e.target.value })}
                />

                <TextField
                  label="Concepto / Motivo del Ajuste"
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={ajusteForm.concepto}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, concepto: e.target.value })}
                  placeholder="Ej: Corrección por compra fallida / Bonificación especial"
                />

                <Button
                  variant="contained"
                  onClick={handleSaveAjuste}
                  disabled={submitting}
                  sx={{ bgcolor: PRIMARY_COLOR, mt: 1 }}
                >
                  {submitting ? <CircularProgress size={20} /> : 'Aplicar Ajuste'}
                </Button>
              </Stack>
            </Box>
          )}

          {/* TAB 2: CANJEAR PREMIO */}
          {tabIndex === 2 && (
            <Box sx={{ maxWidth: 480, mx: 'auto', py: 2 }}>
              <Typography variant="subtitle2" mb={2}>
                Selecciona un premio del catálogo para canjear
              </Typography>

              <Stack spacing={2}>
                <TextField
                  select
                  label="Seleccionar Premio"
                  fullWidth
                  size="small"
                  value={selectedPremio}
                  onChange={(e) => setSelectedPremio(e.target.value)}
                >
                  {premios.map((p) => {
                    const alcanzanPuntos = saldoDisponible >= p.puntos_requeridos;
                    return (
                      <MenuItem key={p.id_incentivo} value={p.id_incentivo} disabled={!alcanzanPuntos}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                          <Typography variant="body2">{p.nombre}</Typography>
                          <Typography variant="caption" fontWeight={700} color={alcanzanPuntos ? 'primary.main' : 'text.disabled'}>
                            {p.puntos_requeridos} pts
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </TextField>

                {selectedPremio && (
                  <Card variant="outlined" sx={{ bgcolor: '#f8fafc', p: 2 }}>
                    {(() => {
                      const item = premios.find(p => p.id_incentivo === selectedPremio);
                      if (!item) return null;
                      return (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" fontWeight={700}>{item.nombre}</Typography>
                          <Typography variant="body2" color="text.secondary">{item.descripcion || 'Sin descripción'}</Typography>
                          <Divider />
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption">Costo en Puntos:</Typography>
                            <Typography variant="caption" fontWeight={700} color="primary.main">{item.puntos_requeridos} pts</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption">Saldo Restante:</Typography>
                            <Typography variant="caption" fontWeight={700}>{saldoDisponible - item.puntos_requeridos} pts</Typography>
                          </Stack>
                        </Stack>
                      );
                    })()}
                  </Card>
                )}

                <Button
                  variant="contained"
                  onClick={handleSaveCanje}
                  disabled={submitting || !selectedPremio}
                  sx={{ bgcolor: PRIMARY_COLOR, mt: 1 }}
                >
                  {submitting ? <CircularProgress size={20} /> : 'Procesar Canje'}
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PuntosMovimientosModal;